import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FaTimesCircle } from "react-icons/fa";
import { useAuth } from "../../context/auth-context";
import CopyToClipboard from "react-copy-to-clipboard";
import Activities from "./Activities";
import { CSVLink } from "react-csv";
import { TailSpin } from "react-loader-spinner";
import { getAssetImage } from "../../constant";
import { noTransaction } from "../../assets/images";
import { IoMdArrowDropdown } from "react-icons/io";
import { BsPatchCheckFill } from "react-icons/bs";
import { MdArrowRight } from "react-icons/md";
import SendUserMessage from "./SendUserMessage";
import AwardPoints from "./AwardPoints";
import SuspendUser from "./SuspendUser";
import MakeInfluencer from "./MakeInfluencer";
import MakeOtcTraderModal from "./MakeOtcTraderModal";
import RemoveOtcTraderModal from "./RemoveOtcTraderModal";
import ReactivateUser from "./ReactivateUser";
import Tasks from "./task/Tasks";
import Rewards from "./Rewards";
import Referrals from "./Referrals";
import Kyc from "./Kyc";
import Addresses from "./Addresses";
import Info from "./Info";
import { BiExport } from "react-icons/bi";
import { formatAmount } from "../../utils/FormatPerson";
import UserTxnsModal from "./UserTxnsModal";
import { toast } from "react-hot-toast";
import { resolveBankNameFromLookup } from "../../lib/api/bankNameResolver";
import {
  apiQueryKey,
  banksLookupMap,
  useApiDeleteMutation,
  useApiPatchMutation,
  useBanksLookupQuery,
  useAdminUserDetailsQuery,
  useAdminUserBalanceSummaryQuery,
  useAdminUserTransactionsPageQuery,
  type UserTransactionStatusFilter,
} from "../../lib/api";
import { UserActivityTransactionsTable } from "./userActivityTransactionsTable";
import { formatOrdinalActivityDate } from "./userActivityDateFormat";
import {
  formatHoldingBalanceAmount,
  holdingBalanceEntries,
} from "./userBalanceSummaryUtils";
import { UserBalanceSummaryPanel } from "./UserBalanceSummaryPanel";
import { UserTransactionsExportModal } from "./UserTransactionsExportModal";
import type { UserActivityTransaction } from "./userActivityTransactionUtils";

function formatTradeRangeNumber(number: number): string {
  if (number >= 1e9) return (number / 1e9).toFixed(0) + "B";
  if (number >= 1e6) return (number / 1e6).toFixed(0) + "M";
  if (number >= 1e3) return (number / 1e3).toFixed(0) + "k";
  if (number >= 100) return (number / 100).toFixed(0) + "h";
  return number.toString();
}

const UserActivity = ({
  setUserActivities,
  kycLevel,
}: {
  setUserActivities: (open: boolean) => void;
  kycLevel: unknown;
}) => {
  const queryClient = useQueryClient();
  const { customerId, setCustomerId } = useAuth();
  const [activeSetting, setActiveSetting] = useState<number>(7);
  const [openAwardModal, setOpenAwardModal] = useState<boolean>(false);
  const [openSuspendUserModal, setOpenSuspendUserModal] =
    useState<boolean>(false);
  const [openSendUserMessageModal, setOpenSendUserMessageModal] =
    useState<boolean>(false);
  const [openButton, setOpenButton] = useState<boolean>(false);
  const [copyMail, setCopyMail] = useState<boolean>(false);
  const [makeInfluencerModal, setMakeInfluencerModal] =
    useState<boolean>(false);
  const [influencerMode, setInfluencerMode] = useState<"create" | "edit">(
    "create"
  );
  const [openMakeOtcTraderModal, setOpenMakeOtcTraderModal] =
    useState<boolean>(false);
  const [openRemoveOtcTraderModal, setOpenRemoveOtcTraderModal] =
    useState<boolean>(false);
  const [openReactivateModal, setOpenReactivateModal] =
    useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [txStatusFilter, setTxStatusFilter] =
    useState<UserTransactionStatusFilter>("");
  const [txnDeets, setTxnDeets] = useState<UserActivityTransaction | null>(null);
  const [openTxnDeetsModal, setOpenTxnDeetsModal] = useState<boolean>(false);
  const [exportOpen, setExportOpen] = useState(false);

  const userDetailsQuery = useAdminUserDetailsQuery(customerId);
  const userDetails = userDetailsQuery.data;
  const user = userDetails?.user as Record<string, unknown> | undefined;
  const profile = user?.profile as Record<string, string> | undefined;
  const account = user?.account as Record<string, string> | undefined;
  const emailObj = user?.email as { address?: string } | undefined;
  const phoneObj = user?.phone as { number?: string } | undefined;
  const kyc = user?.kyc as Record<string, unknown> | undefined;
  const kyc3 = user?.kyc3 as Record<string, unknown> | undefined;
  const surveyDetails = user?.surveyDetails as
    | { volumeRange?: { min?: number; max?: number } }
    | undefined;
  const contracts = user?.contracts as
    | { influencer?: { minTradeVal?: number; compensationPercent?: number } }
    | undefined;
  const otc = user?.otc as { enabled?: boolean } | undefined;
  const roles = user?.roles as string[] | undefined;
  const banks = (user?.banks as Record<string, unknown>[] | undefined) ?? [];
  const walletRecords = userDetails?.wallets ?? [];

  const balanceSummaryQuery = useAdminUserBalanceSummaryQuery(customerId);
  const balanceSummary = balanceSummaryQuery.data;
  const holdingBalances = useMemo(
    () => holdingBalanceEntries(balanceSummary?.currentBalances),
    [balanceSummary?.currentBalances]
  );

  const firstName = profile?.firstName ?? "";
  const lastName = profile?.lastName ?? "";
  const email = emailObj?.address ?? "";
  const username = profile?.username ?? "";
  const phoneNumber = phoneObj?.number ?? "";
  const userRefCode = (user?.referralCode as string) ?? "";
  const kycStatus = (kyc?.status as string) ?? "";
  const userStatus = account?.status ?? "";
  const autoPay = user?.autoPayoutEnabled === true;
  const autoWithdraw = user?.withdrawalApprovalRequired === false;

  const tradeRange = useMemo(() => {
    const min = surveyDetails?.volumeRange?.min;
    const max = surveyDetails?.volumeRange?.max;
    if (min == null && max == null) return { min: "", max: "" };
    return {
      min: min != null ? formatTradeRangeNumber(min) : "",
      max: max != null ? formatTradeRangeNumber(max) : "",
    };
  }, [surveyDetails]);

  const banksLookupQuery = useBanksLookupQuery();
  const banksMap = banksLookupMap(banksLookupQuery.data);

  const transactionsPageQuery = useAdminUserTransactionsPageQuery(
    customerId,
    page,
    txStatusFilter
  );

  const payouts = transactionsPageQuery.data?.transactions ?? [];
  const totalPages = Math.max(
    1,
    transactionsPageQuery.data?.meta?.totalPages ?? 1
  );
  const payoutLoader = transactionsPageQuery.isPending;

  const refreshUserDetails = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("admin-user-details", customerId),
    });
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("admin-user-balance-summary", customerId),
    });
  }, [queryClient, customerId]);

  const refreshUserTransactions = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: apiQueryKey("admin-user-transactions-page", customerId),
    });
  }, [queryClient, customerId]);

  const autopayMutation = useApiPatchMutation<unknown, { enabled: boolean }>(
    `api/settings/autopay/user/${customerId}`,
    {
      body: (v) => ({ autoPayoutEnabled: v.enabled ? "true" : "false" }),
      onSuccess: refreshUserDetails,
    }
  );

  const withdrawApprovalMutation = useApiPatchMutation<
    unknown,
    { required: boolean }
  >(`api/settings/withdrawal-approval/user/${customerId}`, {
    body: (v) => ({ withdrawalApprovalRequired: v.required }),
    onSuccess: refreshUserDetails,
  });

  const removeInfluencerMutation = useApiDeleteMutation(
    () => `api/users/${email}/remove-influencer`,
    {
      onSuccess: () => {
        refreshUserDetails();
        setOpenButton(false);
        toast.success("Influencer removed successfully");
      },
      onError: (e) => {
        const msg =
          (e.response?.data as { message?: string } | undefined)?.message ??
          "Request failed";
        console.log(msg);
      },
    }
  );

  useEffect(() => {
    setPage(1);
    setTxStatusFilter("");
  }, [customerId]);

  const infoData = {
    firstName,
    lastName,
    email,
    phoneNumber,
    kycLevel,
    referralCode: userRefCode,
    kycStatus,
    tradeRange: `${tradeRange.min} - ${tradeRange.max}`,
    username,
    banks: [...banks]
      .sort(
        (a, b) =>
          Number(b.isDefault ?? 0) - Number(a.isDefault ?? 0)
      )
      .map((bank) => {
        const bankName = resolveBankNameFromLookup(
          banksMap,
          bank.bankCode
        );
        return `${bankName} - ${bank.accountName} (${bank.accountNumber})${
          bank.isDefault ? " [Default]" : ""
        }`;
      })
      .join("; "),
  };

  const combinedKycData = userDetails
    ? [
        {
          bvn: (kyc?.data as { bvn?: string })?.bvn,
          verificationId: kyc?.verificationId,
          verificationStatus: kyc?.status,
          kycStatus: kyc3?.status,
          documentOrigin: kyc3?.documentOrigin,
          governmentIdCategory: (
            kyc3?.governmentID as { category?: string } | undefined
          )?.category,
          governmentIdImage: (
            kyc3?.governmentID as { imageURL?: string } | undefined
          )?.imageURL,
          addressApartment: (
            kyc3?.proofOfAddress as
              | { houseAddress?: { apartmentNumber?: string } }
              | undefined
          )?.houseAddress?.apartmentNumber,
          addressStreet: (
            kyc3?.proofOfAddress as
              | { houseAddress?: { streetName?: string } }
              | undefined
          )?.houseAddress?.streetName,
          addressLandmark: (
            kyc3?.proofOfAddress as
              | { houseAddress?: { landmark?: string } }
              | undefined
          )?.houseAddress?.landmark,
          utilityBillCategory: (
            kyc3?.proofOfAddress as
              | { utilityBill?: { category?: string } }
              | undefined
          )?.utilityBill?.category,
          utilityBillImage: (
            kyc3?.proofOfAddress as
              | { utilityBill?: { imageURL?: string } }
              | undefined
          )?.utilityBill?.imageURL,
          livelinessImage: (
            kyc3?.livelinessCheck as { imageURL?: string } | undefined
          )?.imageURL,
          createdAt: formatOrdinalActivityDate(
            (kyc3?.createdAt as string) || ""
          ),
          updatedAt: formatOrdinalActivityDate(
            (kyc3?.updatedAt as string) || ""
          ),
        },
      ]
    : [];

  const referralData = (
    (user?.referrals as Record<string, unknown>[] | undefined) ?? []
  ).map((ref) => {
    const refEmail = ref.email as { address?: string; isVerified?: boolean };
    const lastLogin = ref.lastLogin as
      | {
          device?: { name?: string; uniqueId?: string };
          ipAddress?: string;
          loginTime?: string;
        }
      | undefined;
    return {
      email: refEmail?.address || "",
      isEmailVerified: refEmail?.isVerified ?? false,
      username: (ref.username as string) || "N/A",
      createdAt: ref.createdAt
        ? new Date(ref.createdAt as string).toISOString().split("T")[0]
        : "",
      deviceName: lastLogin?.device?.name || "Unknown",
      deviceId: lastLogin?.device?.uniqueId || "Unknown",
      loginIP: lastLogin?.ipAddress || "Unknown",
      loginTime: lastLogin?.loginTime
        ? new Date(lastLogin.loginTime).toISOString()
        : "Never Logged In",
    };
  });

  const handleChange = () => {
    if (autopayMutation.isPending) return;
    autopayMutation.mutate({ enabled: !autoPay });
  };

  const handleChangeWithdraw = () => {
    if (withdrawApprovalMutation.isPending) return;
    withdrawApprovalMutation.mutate({ required: autoWithdraw });
  };

  const isInfluencer =
    Array.isArray(roles) && roles.includes("influencer");
  const influencerContract = contracts?.influencer;

  const handleRemoveInfluencer = () => {
    if (!email || removeInfluencerMutation.isPending) return;
    removeInfluencerMutation.mutate(undefined);
  };

  const summaryLoading = balanceSummaryQuery.isPending && !balanceSummary;

  return (
    <div className="font-manrope w-full  h-full    bg-[#13181B] ">
      <div className="flex justify-end items-center">
        <FaTimesCircle
          onClick={() => {
            setUserActivities(false);
            setCustomerId("");
          }}
          className="text-[#78C2F7] text-[18px] cursor-pointer"
        />
      </div>
      <div className="flex flex-col md:flex-row justify-start md:justify-between items-start md:items-center w-full mt-6">
        <div className="flex justify-start items-center gap-4">
          <div className="text-[24px] mx-auto  w-[60px] uppercase h-[60px] bg-[#032843] rounded-full text-[#0C84DA] flex justify-center items-center font-bold font-sora ">
            {firstName ? firstName.charAt(0) : "N/"}
            {lastName ? lastName.charAt(0) : "A"}
          </div>
          <div>
            <h4 className="font-sora text-[20px] capitalize text-[#DEE1E6] font-extrabold">
              {firstName ? firstName : "Not"} {lastName ? lastName : "Added"}
            </h4>
            <CopyToClipboard
              text={email}
              onCopy={() => {
                setCopyMail(true);
                setTimeout(() => {
                  setCopyMail(false);
                }, 3000);
              }}
            >
              {copyMail ? (
                <h4 className="text-[12px] mt-2 xs:text-[14px] text-[#869AA9]">
                  Copied
                </h4>
              ) : (
                <h4 className="text-[#0C84DA]  text-[14px] font-medium  cursor-pointer">
                  {email}
                </h4>
              )}
            </CopyToClipboard>
          </div>
        </div>
        <div className="w-full md:w-2/5 mt-6 md:mt-0 lgss:w-1/5 relative">
          <div className="flex w-full items-center justify-end">
            <button
              onClick={() => setOpenButton((prev) => !prev)}
              className="flex  justify-between px-4 outline-none gap-4 h-[48px] rounded-xl w-full md:w-2/3 items-center  text-[#095B97] bg-[#F7F8F9] font-medium text-[16px] "
            >
              Action
              {openButton ? (
                <MdArrowRight className="text-[24px]" />
              ) : (
                <IoMdArrowDropdown className="text-[24px]" />
              )}
            </button>
          </div>
          {openButton && (
            <div
              className={`w-full sidebar absolute bg-[#1C2227] mt-2 flex flex-col   rounded-xl`}
            >
              {userStatus === "ACTIVE" ? (
                <>
                  <button
                    onClick={() => {
                      setOpenButton((prev) => !prev);
                      setOpenSendUserMessageModal(true);
                    }}
                    className="w-full hover:bg-[#273037] text-white text-[16px] font-bold h-[46px] flex justify-center items-center"
                  >
                    Send message
                  </button>
                  <button
                    onClick={() => {
                      setOpenButton((prev) => !prev);
                      setOpenAwardModal(true);
                    }}
                    className="w-full hover:bg-[#273037] text-white text-[16px] font-bold h-[46px] flex justify-center items-center"
                  >
                    Award points
                  </button>
                  {!otc?.enabled ? (
                    <button
                      onClick={() => {
                        setOpenButton((prev) => !prev);
                        setOpenMakeOtcTraderModal(true);
                      }}
                      className="w-full hover:bg-[#273037] text-white text-[16px] font-bold h-[46px] flex justify-center items-center"
                    >
                      Make OTC trader
                    </button>
                  ) : (
                    <button
                      onClick={() => {
                        setOpenButton((prev) => !prev);
                        setOpenRemoveOtcTraderModal(true);
                      }}
                      className="w-full hover:bg-[#273037] text-white text-[16px] font-bold h-[46px] flex justify-center items-center"
                    >
                      Remove OTC trader
                    </button>
                  )}

                  {!isInfluencer && (
                    <button
                      onClick={() => {
                        setOpenButton((prev) => !prev);
                        setInfluencerMode("create");
                        setMakeInfluencerModal(true);
                      }}
                      className="w-full hover:bg-[#273037] text-white  text-[16px] font-bold h-[46px] flex justify-center items-center"
                    >
                      Make Influencer
                      <BsPatchCheckFill className="text-[20px] ml-2" />
                    </button>
                  )}
                  {isInfluencer && (
                    <>
                      <button
                        onClick={() => {
                          setOpenButton((prev) => !prev);
                          setInfluencerMode("edit");
                          setMakeInfluencerModal(true);
                        }}
                        className="w-full hover:bg-[#273037] text-white text-[16px] font-bold h-[46px] flex justify-center items-center"
                      >
                        Edit Influencer
                        <BsPatchCheckFill className="text-[20px] ml-2" />
                      </button>
                      <button
                        onClick={handleRemoveInfluencer}
                        className="w-full hover:bg-[#273037] text-[#DF4E4E] text-[16px] font-bold h-[46px] flex justify-center items-center"
                        disabled={removeInfluencerMutation.isPending}
                      >
                        {removeInfluencerMutation.isPending
                          ? "Removing..."
                          : "Remove Influencer"}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setOpenButton((prev) => !prev);
                      setOpenSuspendUserModal(true);
                    }}
                    className="text-[15px]  h-[46px]  font-semibold text-[#DF4E4E]"
                  >
                    Suspend user
                  </button>
                </>
              ) : userStatus === "SUSPENDED" ? (
                <button
                  onClick={() => {
                    setOpenButton((prev) => !prev);
                    setOpenReactivateModal(true);
                  }}
                  className="text-[15px]  h-[46px]  font-semibold text-[#60B7F6]"
                >
                  Reactivate User
                </button>
              ) : null}
            </div>
          )}
        </div>
      </div>
      <div className="mt-5 border-b border-gray-800">
        <h4 className="px-4 py-2 text-[15px] font-bold text-white">
          Holding Balance
        </h4>
      </div>
      {summaryLoading ? (
        <div className="mt-10 flex h-[140px] items-center justify-center">
          <TailSpin color="#0B75C2" height={40} width={40} />
        </div>
      ) : holdingBalances.length === 0 ? (
        <div className="mt-10 flex h-[140px] items-center justify-center rounded-xl border border-[#2C363E]">
          <p className="text-[13px] text-[#869AA9]">No holding balances</p>
        </div>
      ) : (
        <div className="w-full mt-10 -mx-1 px-1 overflow-x-auto overflow-y-hidden scroll-smooth scrollbar-thin scrollbar-track-[#1C2227] scrollbar-thumb-[#27313A] hover:scrollbar-thumb-[#3A4852] pb-4 [scrollbar-gutter:stable]">
          <div className="flex w-max min-w-full snap-x snap-mandatory gap-3 pr-2 sm:gap-4 sm:pr-4">
            {holdingBalances.map(({ key, value }) => {
              const coinImage = getAssetImage(key);
              const label = key.toUpperCase();

              return (
                <div
                  key={key}
                  className="w-[240px] flex-shrink-0 snap-start rounded-xl border border-[#27313A] bg-[#1C2227] p-4 shadow-sm transition-colors hover:border-[#2C363E] xs:w-[260px] sm:w-[280px] sm:p-5"
                >
                  <div className="flex items-center justify-start gap-2.5">
                    <div className="flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#0D1115]">
                      {coinImage ? (
                        <img
                          src={coinImage}
                          className="h-full w-full object-contain"
                          alt={label}
                        />
                      ) : (
                        <span className="text-[10px] font-bold text-[#869AA9]">
                          {label.slice(0, 3)}
                        </span>
                      )}
                    </div>
                    <h4 className="truncate text-[12px] font-bold text-white sm:text-[13px]">
                      {label}
                    </h4>
                  </div>
                  <h4 className="mt-3 break-words font-sora text-[17px] font-bold text-[#BEE1FB] sm:text-[20px]">
                    {formatHoldingBalanceAmount(key, value)}
                  </h4>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="w-full flex justify-between items-center mt-8">
        <h4 className="text-white font-sora font-semibold text-[12px]">
          Activity
        </h4>
        <div className="flex gap-4 items-center">
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {autopayMutation.isPending ? (
                <TailSpin color="#0B75C2" height={20} width={20} />
              ) : (
                <div
                  onClick={handleChange}
                  className={`flex w-8  cursor-pointer h-4   rounded-full transition-all duration-500 ${
                    autoPay === true ? "bg-secondary_button" : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`h-4 w-4   rounded-full transition-all duration-500 bg-gray-100 ${
                      autoPay === true ? "ml-4" : ""
                    }`}
                  ></span>
                </div>
              )}
              <h4 className="uppercase font-sora font-semibold text-[#869AA9] text-[10px]">
                Auto Pay:{" "}
                <span className={autoPay === true ? "text-[#359943]" : ""}>
                  {autoPay === true ? " ON" : " OFF"}
                </span>
              </h4>
            </div>
          </div>
          <h4 className="text-[#3A4852]">|</h4>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-2">
              {withdrawApprovalMutation.isPending ? (
                <TailSpin color="#0B75C2" height={20} width={20} />
              ) : (
                <div
                  onClick={handleChangeWithdraw}
                  className={`flex w-8  cursor-pointer h-4   rounded-full transition-all duration-500 ${
                    autoWithdraw === true
                      ? "bg-secondary_button"
                      : "bg-gray-600"
                  }`}
                >
                  <span
                    className={`h-4 w-4   rounded-full transition-all duration-500 bg-gray-100 ${
                      autoWithdraw === true ? "ml-4" : ""
                    }`}
                  ></span>
                </div>
              )}
              <h4 className="uppercase font-sora font-semibold text-[#869AA9] text-[10px]">
                Auto Withdrawal:{" "}
                <span className={autoWithdraw === true ? "text-[#359943]" : ""}>
                  {autoWithdraw === true ? " ON" : " OFF"}
                </span>
              </h4>
            </div>
          </div>
          <h4 className="text-[#3A4852]">|</h4>
          {activeSetting === 1 ? (
            <button
              type="button"
              onClick={() => setExportOpen(true)}
              className="flex items-center justify-center gap-2"
            >
              <BiExport className="text-[white]" />
              <h4 className="mt-1 font-sora text-[10px] font-semibold uppercase text-[#869AA9]">
                Export
              </h4>
            </button>
          ) : (
            <CSVLink
              data={
                activeSetting === 7
                  ? [infoData]
                  : activeSetting === 6
                  ? combinedKycData
                  : activeSetting === 5
                  ? referralData
                  : []
              }
              filename={`${firstName} ${lastName}-${
                activeSetting === 7
                  ? "info"
                  : activeSetting === 6
                  ? "kyc"
                  : activeSetting === 5
                  ? "referrals"
                  : ""
              }-list.csv`}
              className="flex items-center justify-center gap-2"
            >
              <BiExport className="text-[white]" />
              <h4 className="mt-1 font-sora text-[10px] font-semibold uppercase text-[#869AA9]">
                Export
              </h4>
            </CSVLink>
          )}
        </div>
      </div>
      <div className="flex justify-center  gap-6 mt-4 w-full items-start">
        <div className="w-full h-full ">
          <div className="bg-[#0D0D0D] w-full flex overflow-x-auto lgss:overflow-x-hidden justify-start items-center gap-6 pr-12 h-[67px] mt-4">
            <h4
              onClick={() => setActiveSetting(7)}
              className={
                activeSetting === 7
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Info
            </h4>
            <h4
              onClick={() => setActiveSetting(8)}
              className={
                activeSetting === 8
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Addresses
            </h4>
            <h4
              onClick={() => setActiveSetting(9)}
              className={
                activeSetting === 9
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Summary
            </h4>
            <h4
              onClick={() => setActiveSetting(1)}
              className={
                activeSetting === 1
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Transactions
            </h4>
            <h4
              onClick={() => setActiveSetting(2)}
              className={
                activeSetting === 2
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Activities
            </h4>
            <h4
              onClick={() => setActiveSetting(3)}
              className={
                activeSetting === 3
                  ? "border-secondary_button cursor-pointer flex justify-center items-center h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[14px] cursor-pointer  text-[#C6D0D7]  "
              }
            >
              Rewards
            </h4>
            <h4
              onClick={() => setActiveSetting(4)}
              className={
                activeSetting === 4
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Task
            </h4>
            <h4
              onClick={() => setActiveSetting(5)}
              className={
                activeSetting === 5
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              Referrals
            </h4>
            <h4
              onClick={() => setActiveSetting(6)}
              className={
                activeSetting === 6
                  ? "border-secondary_button flex justify-center items-center cursor-pointer h-full px-6 text-[16px] font-semibold text-secondary_button border-b-2 "
                  : "  px-6 text-[16px]  text-[#C6D0D7]  cursor-pointer"
              }
            >
              KYC
            </h4>
          </div>

          {activeSetting === 8 ? (
            <Addresses
              wallets={walletRecords}
              isLoading={userDetailsQuery.isPending}
            />
          ) : activeSetting === 9 ? (
            summaryLoading ? (
              <div className="mt-8 flex h-[200px] items-center justify-center">
                <TailSpin color="#0B75C2" height={40} width={40} />
              </div>
            ) : (
              <UserBalanceSummaryPanel summary={balanceSummary} />
            )
          ) : activeSetting === 1 ? (
            <div className="w-full ">
              <div className="flex  items-center flex-col lgss:flex-row justify-start lgss:justify-between  w-full"></div>
              <div className="w-full">
                <UserActivityTransactionsTable
                  transactions={payouts}
                  isLoading={payoutLoader}
                  page={page}
                  totalPages={totalPages}
                  transactionCount={
                    transactionsPageQuery.data?.meta?.transactionsCount ?? 0
                  }
                  statusFilter={txStatusFilter}
                  onStatusFilterChange={(status) => {
                    setTxStatusFilter(status);
                    setPage(1);
                  }}
                  onPageChange={(next) => setPage(next)}
                  onRowClick={(tx) => {
                    setTxnDeets(tx);
                    setOpenTxnDeetsModal(true);
                  }}
                  emptyImage={noTransaction}
                />
              </div>
            </div>
          ) : activeSetting === 2 ? (
            <Activities customerId={customerId} />
          ) : activeSetting === 3 ? (
            <Rewards customerId={customerId} />
          ) : activeSetting === 4 ? (
            <Tasks customerId={customerId} />
          ) : activeSetting === 5 ? (
            <Referrals referrals={user?.referrals as never} />
          ) : activeSetting === 6 ? (
            <Kyc customerId={customerId} />
          ) : activeSetting === 7 ? (
            <Info
              userFn={firstName}
              userLn={lastName}
              userPhoneNo={phoneNumber}
              userMail={email}
              kycLevel={kycLevel}
              userRefCode={userRefCode}
              kycStatus={kycStatus}
              tradeRange={tradeRange}
              username={username}
              banks={banks}
            />
          ) : null}
        </div>
      </div>
      {openSendUserMessageModal && (
        <SendUserMessage
          setOpenSendUserMessageModal={setOpenSendUserMessageModal}
          customerId={customerId}
        />
      )}
      {openAwardModal && (
        <AwardPoints
          setOpenAwardModal={setOpenAwardModal}
          customerId={customerId}
        />
      )}
      {openSuspendUserModal && (
        <SuspendUser
          setOpenSuspendUserModal={setOpenSuspendUserModal}
          customerId={customerId}
          onSuccess={refreshUserDetails}
        />
      )}
      {makeInfluencerModal && (
        <MakeInfluencer
          setMakeInfluencerModal={setMakeInfluencerModal}
          userMail={email}
          userFn={firstName}
          userLn={lastName}
          onSuccess={refreshUserDetails}
          mode={influencerMode}
          initialMinTradeVal={influencerContract?.minTradeVal}
          initialCompensationPercent={influencerContract?.compensationPercent}
        />
      )}
      {openMakeOtcTraderModal && (
        <MakeOtcTraderModal
          open={openMakeOtcTraderModal}
          onClose={() => setOpenMakeOtcTraderModal(false)}
          userId={customerId}
          userName={
            [firstName, lastName].filter(Boolean).join(" ") || undefined
          }
          onSuccess={refreshUserDetails}
        />
      )}
      {openRemoveOtcTraderModal && (
        <RemoveOtcTraderModal
          open={openRemoveOtcTraderModal}
          onClose={() => setOpenRemoveOtcTraderModal(false)}
          userId={customerId}
          userName={
            [firstName, lastName].filter(Boolean).join(" ") || undefined
          }
          onSuccess={refreshUserDetails}
        />
      )}
      {openReactivateModal && (
        <ReactivateUser
          setOpenReactivateModal={setOpenReactivateModal}
          customerId={customerId}
          userFn={firstName}
          userLn={lastName}
          onSuccess={refreshUserDetails}
        />
      )}
      {openTxnDeetsModal && txnDeets ? (
        <UserTxnsModal
          onClose={() => {
            setOpenTxnDeetsModal(false);
            setTxnDeets(null);
          }}
          txnDeets={txnDeets}
          email={email}
          firstname={firstName}
          lastName={lastName}
          onPayoutMarkedPaid={refreshUserTransactions}
        />
      ) : null}
      <UserTransactionsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        userId={customerId}
        firstName={firstName}
        lastName={lastName}
      />
    </div>
  );
};

export default UserActivity;
